import { useEffect, useMemo, useState } from 'react'
import Card from '@mui/material/Card'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme, styled } from '@mui/material/styles'
import { DatePicker } from 'antd'
import dayjs from 'dayjs'
import { Icon } from '@iconify/react'
import Image from 'next/image'
import { useTranslation } from 'src/utils/i18n'
import EmptyState, { EMPTY_TYPES } from 'src/components/EmptyState'
import styles from './chat-history.module.scss'
import ContentLoading from 'src/components/ContentLoading'

const CustomDatePicker = styled(DatePicker)`
  .ant-picker-input > input {
    color: var(--02484-b-60, #484b60) !important;
    font-family: Poppins, sans-serif !important;
    font-size: 14px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    line-height: 22px !important;
    text-align: left !important;
  }

  .ant-picker-input > input::placeholder {
    color: #bfbfbf !important;
  }

  .ant-picker-suffix {
    opacity: 1 !important;
    color: var(--02484-b-60, #484b60);
  }
`

export { EMPTY_TYPES }

export const AI_CATEGORIES = {
  CHATGPT: 'CHATGPT',
  GEMINI: 'GEMINI',
  DEEPSEEK: 'DEEPSEEK',
  CLAUDE: 'CLAUDE',
  PERPLEXITY: 'PERPLEXITY',
  COPILOT: 'COPILOT',
  POE: 'POE',
  CHARACTER_AI: 'CHARACTER_AI',
  PI_AI: 'PI_AI'
}

const ASSISTANT_LABEL = {
  CHATGPT: 'ChatGPT',
  GEMINI: 'Gemini',
  DEEPSEEK: 'DeepSeek',
  CLAUDE: 'Claude',
  PERPLEXITY: 'Perplexity',
  COPILOT: 'Copilot',
  POE: 'Poe',
  CHARACTER_AI: 'Character.AI',
  PI_AI: 'Pi'
}

/** Map /dashboard/auth/all contents → session list items */
export const mapAuthContentsToSessions = (contents = [], category = 'CHATGPT') => {
  if (!Array.isArray(contents)) return []

  return contents.map((item, index) => {
    const ts = Number(item.time)
    const time = Number.isFinite(ts) ? dayjs(ts) : dayjs()
    const input = item.input_text || ''
    const output = item.output_text || ''
    const assistantName = ASSISTANT_LABEL[category] || category

    return {
      id: `${category}-${ts || index}-${index}`,
      title: input || output || assistantName,
      listDate: time.format('YYYY/M/D'),
      userName: 'User',
      assistantName,
      userMessage: input,
      assistantMessage: output,
      timestamp: time.format('YYYY-MM-DD HH:mm:ss'),
      category,
      app: item.app || ''
    }
  })
}

/**
 * Universal Chat History layout
 * - PC: left session list + right one user msg / one AI reply
 * - Mobile: list first → tap item → detail
 */
const ChatHistory = ({
  sessions = [],
  categories = [],
  activeCategory,
  onCategoryChange,
  loading = false,
  searchPlaceholder,
  emptyType = EMPTY_TYPES.NO_DATA,
  className = '',
  onDateChange
}) => {
  const { t } = useTranslation('common')
  const theme = useTheme()
  const mdAbove = useMediaQuery(theme.breakpoints.up('md'))

  const [search, setSearch] = useState('')
  const [filterDate, setFilterDate] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [mobileShowDetail, setMobileShowDetail] = useState(false)

  useEffect(() => {
    setSelectedId(sessions[0]?.id ?? null)
    setMobileShowDetail(false)
  }, [sessions])

  const filteredSessions = useMemo(() => {
    const keyword = search.trim().toLowerCase()

    return sessions.filter(item => {
      const matchSearch =
        !keyword ||
        (item.title || '').toLowerCase().includes(keyword) ||
        (item.userMessage || '').toLowerCase().includes(keyword) ||
        (item.assistantMessage || '').toLowerCase().includes(keyword)

      // 上层接管日期筛选时（onDateChange），列表已由接口按日期返回，这里不再本地按天过滤
      if (!filterDate || onDateChange) return matchSearch

      return matchSearch && dayjs(item.timestamp).isSame(filterDate, 'day')
    })
  }, [sessions, search, filterDate, onDateChange])

  const selected = useMemo(
    () => filteredSessions.find(item => item.id === selectedId) || filteredSessions[0] || null,
    [filteredSessions, selectedId]
  )

  const handleSelect = id => {
    setSelectedId(id)
    if (!mdAbove) setMobileShowDetail(true)
  }

  // 日期变化：通知上层带 startTime/endTime 重新请求接口
  const applyDate = value => {
    setFilterDate(value || null)
    onDateChange?.(value || null)
  }

  const handlePrevDay = () => {
    applyDate((filterDate || dayjs()).subtract(1, 'day'))
  }

  const handleNextDay = () => {
    applyDate((filterDate || dayjs()).add(1, 'day'))
  }

  const placeholder =
    searchPlaceholder ||
    (mdAbove ? t('Search Browser History') || 'Search Browser History' : t('Search') || 'Search')

  const renderCategories = () => {
    if (!categories.length) return null

    return (
      <div className={styles.categoryRow}>
        {categories.map(cat => {
          const active = cat.category === activeCategory

          return (
            <button
              key={cat.category}
              type='button'
              className={`${styles.categoryItem} ${active ? styles.categoryItemActive : ''}`}
              onClick={() => onCategoryChange?.(cat.category)}
            >
              {cat.icon ? (
                <Image src={cat.icon} width={22} height={22} alt={cat.name} />
              ) : cat.iconify ? (
                <Icon icon={cat.iconify} width={18} height={18} />
              ) : (
                <span className={styles.categoryDot} />
              )}
              <span>{cat.name}</span>
            </button>
          )
        })}
      </div>
    )
  }

  const renderSearch = (extraClass = '') => (
    <div className={`${styles.searchBox} ${extraClass}`}>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={placeholder}
        className={styles.searchInput}
      />
      <svg
        className={styles.searchIcon}
        xmlns='http://www.w3.org/2000/svg'
        width='20'
        height='20'
        viewBox='0 0 20 20'
        fill='none'
        aria-hidden
      >
        <path
          d='M8.90039 3.49072C12.2141 3.49072 14.9004 6.17701 14.9004 9.49072C14.9004 10.5198 14.6411 11.4881 14.1846 12.3345L16.7969 14.7739C17.2005 15.1509 17.2005 15.7622 16.7969 16.1392L16.7031 16.2261L16.625 16.2935C16.219 16.6023 15.6195 16.5793 15.2412 16.2261L12.8564 13.9985C11.8001 14.9262 10.4167 15.4907 8.90039 15.4907C5.58682 15.4906 2.90039 12.8043 2.90039 9.49072C2.90039 6.17712 5.58682 3.49089 8.90039 3.49072ZM8.90039 4.99072C6.41525 4.99089 4.40039 7.00554 4.40039 9.49072C4.40039 11.9759 6.41525 13.9906 8.90039 13.9907C10.2777 13.9907 11.5095 13.3708 12.335 12.396C12.3505 12.375 12.3673 12.3546 12.3848 12.3345L12.3994 12.3188C13.025 11.5459 13.4004 10.5625 13.4004 9.49072C13.4004 7.00544 11.3857 4.99072 8.90039 4.99072Z'
          fill='#6B6F88'
        />
      </svg>
    </div>
  )

  const renderDatePicker = (withArrows = false) => (
    <div className={styles.dateGroup}>
      {withArrows && (
        <button type='button' className={styles.dateArrow} onClick={handlePrevDay} aria-label='previous day'>
          <Icon icon='mdi:chevron-left' width={22} />
        </button>
      )}
      {withArrows ? (
        <CustomDatePicker
          value={filterDate}
          onChange={applyDate}
          allowClear
          inputReadOnly
          format='YYYY-MM-DD'
          placeholder='YYYY-MM-DD'
          className={styles.datePicker}
          prefix={<Icon icon='mdi:calendar-month-outline' width={18} className={styles.dateIcon} />}
          suffixIcon={null}
        />
      ) : (
        <div className={styles.mobileDatePickerWrap}>
          <CustomDatePicker
            value={filterDate}
            onChange={applyDate}
            allowClear={false}
            inputReadOnly
            format='YYYY-MM-DD'
            placeholder='YYYY-MM-DD'
            className={styles.datePicker}
            prefix={null}
            suffixIcon={null}
          />
          {filterDate ? (
            <button
              type='button'
              className={styles.mobileDateAction}
              onClick={() => applyDate(null)}
              aria-label='clear date'
            >
              <Icon icon='mdi:close' width={18} className={styles.dateIcon} />
            </button>
          ) : (
            <span className={styles.mobileDateIcon} aria-hidden>
              <Icon icon='mdi:calendar-month-outline' width={18} className={styles.dateIcon} />
            </span>
          )}
        </div>
      )}
      {withArrows && (
        <button type='button' className={styles.dateArrow} onClick={handleNextDay} aria-label='next day'>
          <Icon icon='mdi:chevron-right' width={22} />
        </button>
      )}
    </div>
  )

  const renderList = () => {
    if (loading) {
      return <ContentLoading />
    }

    if (filteredSessions.length === 0) {
      return null
    }

    return (
      <ul className={styles.list}>
        {filteredSessions.map(item => {
          const active = selected?.id === item.id

          return (
            <li
              key={item.id}
              className={`${styles.listItem} ${active ? styles.listItemActive : ''}`}
              onClick={() => handleSelect(item.id)}
              role='button'
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleSelect(item.id)
                }
              }}
            >
              <span className={styles.listTitle}>{item.title}</span>
              <span className={styles.listDate}>{item.listDate}</span>
            </li>
          )
        })}
      </ul>
    )
  }

  const renderMessages = session => {
    if (loading) {
      return <ContentLoading />
    }

    if (!session) {
      if (filteredSessions.length === 0) {
        return <EmptyState type={emptyType || EMPTY_TYPES.NO_DATA} className={styles.emptyState} />
      }

      return <div className={styles.emptyWrap} />
    }

    return (
      <div className={styles.messageList}>
        {/* User question — right */}
        <div className={`${styles.messageRow} ${styles.messageRight}`}>
          <div className={styles.sender}>{session.userName}</div>
          <div className={`${styles.bubble} ${styles.bubbleUser}`}>{session.userMessage}</div>
          <div className={styles.timestamp}>{session.timestamp}</div>
        </div>

        {/* AI answer — left */}
        <div className={`${styles.messageRow} ${styles.messageLeft}`}>
          <div className={styles.sender}>{session.assistantName}</div>
          <div className={`${styles.bubble} ${styles.bubbleAssistant}`}>{session.assistantMessage}</div>
          <div className={styles.timestamp}>{session.timestamp}</div>
        </div>
      </div>
    )
  }

  const heightClass =
    'Bxl:h-[calc(var(--vh2)*100-90px)] Bxl:max-h-[calc(var(--vh2)*100-90px)] Bxls:Headers5:h-[calc(var(--vh2)*100-145px)] Bxls:Headers5:max-h-[calc(var(--vh2)*100-145px)] Header5:big:h-[calc(var(--vh2)*100-210px)] Header5:big:max-h-[calc(var(--vh2)*100-210px)] bigs:h-[calc(var(--vh2)*100-270px)] bigs:max-h-[calc(var(--vh2)*100-270px)]'

  // —— Mobile: list view ——
  if (!mdAbove && !mobileShowDetail) {
    const hasNoFilteredResults = !loading && filteredSessions.length === 0
    const showFullEmpty = hasNoFilteredResults && sessions.length === 0

    return (
      <Card className={`${styles.root} ${heightClass} ${className}`}>
        {!showFullEmpty ? (
          <div className={styles.mobileToolbar}>
            {renderSearch(styles.searchGrow)}
            {renderDatePicker(false)}
          </div>
        ) : null}
        {showFullEmpty ? (
          <EmptyState type={emptyType || EMPTY_TYPES.NO_DATA} className={styles.emptyState} />
        ) : (
          <>
            {renderCategories()}
            <div className={styles.mobileListBody}>
              {hasNoFilteredResults ? (
                <EmptyState type={emptyType || EMPTY_TYPES.NO_DATA} className={styles.emptyState} />
              ) : (
                renderList()
              )}
            </div>
          </>
        )}
      </Card>
    )
  }

  // —— Mobile: detail view ——
  if (!mdAbove && mobileShowDetail) {
    return (
      <Card className={`${styles.root} ${heightClass} ${className}`}>
        <div className={styles.mobileDetailHeader}>
          <button
            type='button'
            className={styles.backBtn}
            onClick={() => setMobileShowDetail(false)}
            aria-label='back'
          >
            <Icon icon='mdi:chevron-left' width={24} />
          </button>
        </div>
        <div className={styles.detailBody}>{renderMessages(selected)}</div>
      </Card>
    )
  }

  // —— PC: top toolbar + bottom split ——
  return (
    <Card className={`${styles.root} ${heightClass} ${className}`}>
      <div className={styles.pcToolbar}>
        {renderSearch()}
        {renderDatePicker(true)}
      </div>
      {renderCategories()}
      <div className={styles.pcLayout}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarBody}>{renderList()}</div>
        </aside>

        <section className={styles.main}>
          <div className={styles.detailBody}>{renderMessages(selected)}</div>
        </section>
      </div>
    </Card>
  )
}

export default ChatHistory
